import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { TInputAppState } from '../reducer';
import { TranslateService } from '@ngx-translate/core';
import { getUpdateDetails } from './remote-deployment-db.query';
import {
  TAppUpdatesDTO,
  TRemoteDeploymentCheck,
  TRemoteDeploymentUpdates,
  TUserActionForAppUpdate,
  TBgImageSyncErrorStatus,
  TBgEventSyncErrorStatus,
} from '@monorepo/types';
import { take } from 'rxjs/operators';
import { CheckForAppUpdates, ReFetchAppMasterAndClearState } from './remote-deployment-db.actions';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { BuildUtils, EventUtil, InputAppAPIHelperUtil } from '@monorepo/utils';
import { MasterdataListDBFacade } from '../master-data-list-db/master-data-list-db.facade';
import { AppUpdatesComponent } from '@monorepo/ui';
import { AlertController, ModalController, AlertButton } from '@ionic/angular';
import { InputAppImageDBFacade } from '../image-db/image-db.facade';
import { BulkCountAppFacade } from '../bulk-count-app-db/bulk-count-app-db.facade';
import { BundleQcDBFacade } from '../bundle-qc-db/bundle-qc.facade';
import { InitStoreFacade } from '../init-db/init-db.facade';
const INIT_RETRY_COUNT = 3;
@Injectable({
  providedIn: 'root',
})
export class RemoteDeploymentStoreFacade {
  remainingRetryCount = INIT_RETRY_COUNT;
  constructor(
    private store: Store<TInputAppState>,
    private translate: TranslateService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private masterdataListDBFacade: MasterdataListDBFacade,
    private bulkCountAppFacade: BulkCountAppFacade,
    private modalCtrl: ModalController,
    private imageFacade: InputAppImageDBFacade,
    private inputAppHelper: InputAppAPIHelperUtil,
    private alertCtrl: AlertController,
    private bundleQcDBFacade: BundleQcDBFacade,
    private initStoreFacade: InitStoreFacade,
  ) {}

  init() {
    this.bundleQcDBFacade.getAppUpdateSubscription().subscribe((res) => {
      if (res.syncResult.status === 'ALL_SYNCED') {
        switch (res.remoteDeploymentcheck.appIdentifier) {
          case 'bundle_qc_v2': {
            this.inputAppClientDBFacade.forceSyncEventsAndUpdateApp(false, res.remoteDeploymentcheck, false);
            break;
          }
          default: {
            this.inputAppHelper.showPopup(
              this.translate.instant('remote_deployment.config_error'),
              this.translate.instant('remote_deployment.update_not_configured'),
            );
          }
        }
      } else this.showErrorPopup(res.remoteDeploymentcheck, res.syncResult.status);
    });

    this.imageFacade.getAppUpdateSubscription().subscribe((res) => {
      if (res.syncResult.status === 'ALL_SYNCED') {
        switch (res.remoteDeploymentCheck.appIdentifier) {
          case 'endline_qc_v2':
          case 'bundle_qc_v2': {
            this.inputAppClientDBFacade.forceSyncEventsAndUpdateApp(false, res.remoteDeploymentCheck, false);
            break;
          }
          case 'per_piece_qc_v2':
          case 'per_piece_line_in_v2':
          case 'md_point_per_piece_qc_v2':
          case 'plan_qc_v2':
          case 'roaming_qc_v2': {
            this.inputAppClientDBFacade.forceSyncEventsAndUpdateApp(true, res.remoteDeploymentCheck, false);
            break;
          }
          default: {
            this.inputAppHelper.showPopup(
              this.translate.instant('remote_deployment.config_error'),
              this.translate.instant('remote_deployment.update_not_configured'),
            );
          }
        }
      } else this.showErrorPopup(res.remoteDeploymentCheck, res.syncResult.status);
    });
    this.inputAppClientDBFacade.getAppUpdateSubscription().subscribe((res) => {
      if (res.syncResult.status === 'ALL_SYNCED') {
        this.store.dispatch(new ReFetchAppMasterAndClearState(res.remoteDeploymentcheck));
      } else this.showErrorPopup(res.remoteDeploymentcheck, res.syncResult.status);
    });
    this.bulkCountAppFacade.getAppUpdateSubscription().subscribe((res) => {
      if (res.syncResult.status === 'ALL_SYNCED') {
        this.store.dispatch(new ReFetchAppMasterAndClearState(res.remoteDeploymentcheck));
      } else this.showErrorPopup(res.remoteDeploymentcheck, res.syncResult.status);
    });
  }

  getUpdateDetails() {
    return this.store.pipe(select(getUpdateDetails));
  }

  checkForUpdates(remoteDeploymentCheck: TRemoteDeploymentCheck) {
    const subscription = this.inputAppClientDBFacade
      .getConfigurations()
      .pipe(take(1))
      .subscribe((res) => {
        if (
          (remoteDeploymentCheck.type === 'CHECK_FROM_INIT' && remoteDeploymentCheck.isReload) ||
          !res.hasEnabledForRemoteAppUpdates
        ) {
          this.initAppData(remoteDeploymentCheck);
        } else {
          this.remainingRetryCount = INIT_RETRY_COUNT;
          this.store.dispatch(new CheckForAppUpdates(remoteDeploymentCheck));
        }
      });
    subscription.unsubscribe();
  }

  retryForUpdates(remoteDeploymentCheck: TRemoteDeploymentCheck) {
    const subscription = this.getUpdateDetails()
      .pipe(take(1))
      .subscribe((res) => {
        if (res.updatesInfo) {
          this.syncPendingDataAndUpadate(remoteDeploymentCheck);
        } else this.store.dispatch(new CheckForAppUpdates(remoteDeploymentCheck));
      });
    subscription.unsubscribe();
  }

  hasNewUpdates(latestVersionAvailable: string) {
    const newVersionArr = latestVersionAvailable.split('.');
    const currentVersionArr = BuildUtils.APP_VERSION.split('.');
    if (newVersionArr.length === currentVersionArr.length) {
      try {
        for (let i = 0; i < newVersionArr.length; i++) {
          const newVersion = parseInt(newVersionArr[i]);
          const currentVersion = parseInt(currentVersionArr[i]);
          if (newVersion > currentVersion) return true;
          else if (newVersion < currentVersion) return false;
        }
        return false;
      } catch (error) {
        return false;
      }
    } else {
      return false;
    }
  }

  async showErrorPopup(
    remoteDeploymentCheck: TRemoteDeploymentCheck,
    error: TBgImageSyncErrorStatus | TBgEventSyncErrorStatus,
  ) {
    this.getUpdateDetails()
      .pipe(take(1))
      .subscribe((res) => {
        const hideUpdateLaterTillRetriesExhausted = !!res.updatesInfo && res.updatesInfo.featureType !== 'optional';
        switch (error) {
          case 'NO_INTERNET_FOR_SYNCING_PENDING_DEFECTIVE_IMAGES':
          case 'NO_INTERNET_FOR_SYNCING_PENDING_EVENTS': {
            this.showErrorPopupWithResolvedSubHeader(
              remoteDeploymentCheck,
              this.translate.instant('remote_deployment.check_internet'),
              true,
              this.translate.instant('remote_deployment.sync_failed'),
              hideUpdateLaterTillRetriesExhausted,
            );
            break;
          }
          case 'PENDING_DEFECTIVE_IMAGES_SYNC_FAILED':
          case 'SYNCING_PENDING_EVENTS_FAILED': {
            this.showErrorPopupWithResolvedSubHeader(
              remoteDeploymentCheck,
              this.translate.instant('remote_deployment.sync_issue_retry'),
              true,
              this.translate.instant('remote_deployment.sync_failed'),
              hideUpdateLaterTillRetriesExhausted,
            );
            break;
          }
          case 'ALL_SYNCED_WITH_VALIDATION_ERRORS': {
            this.showErrorPopupWithResolvedSubHeader(
              remoteDeploymentCheck,
              this.translate.instant('remote_deployment.sync_validation_continue'),
              false,
              this.translate.instant('remote_deployment.sync_failed'),
              hideUpdateLaterTillRetriesExhausted,
            );
            break;
          }
          default:
            break;
        }
      });
  }

  async showErrorPopupWithResolvedSubHeader(
    remoteDeploymentCheck: TRemoteDeploymentCheck,
    header: string,
    reduceRetryCount: boolean,
    title: string,
    hideUpdateLaterTillRetriesExhausted: boolean,
  ) {
    const updateLaterButton: AlertButton = {
      text: this.translate.instant('remote_deployment.update_later'),
      handler: () => {
        this.initAppData(remoteDeploymentCheck);
      },
    };
    const retryOrContinueButton: AlertButton = {
      text: reduceRetryCount
        ? this.translate.instant('remote_deployment.retry')
        : this.translate.instant('remote_deployment.continue'),
      handler: () => {
        if (reduceRetryCount) this.remainingRetryCount--;
        this.retryForUpdates(remoteDeploymentCheck);
      },
    };
    const alert = await this.alertCtrl.create({
      header: title,
      subHeader: header,
      buttons:
        this.remainingRetryCount === 0
          ? [updateLaterButton]
          : hideUpdateLaterTillRetriesExhausted
          ? [retryOrContinueButton]
          : [updateLaterButton, retryOrContinueButton],
      cssClass: 'app-update-error',
      backdropDismiss: false,
    });
    alert.present();
  }

  async showAppUpdatesPopup(remoteDeploymentCheck: TRemoteDeploymentCheck, appUpdates: TRemoteDeploymentUpdates) {
    if (!this.hasNewUpdates(appUpdates.latestVersionAvailable)) {
      if (remoteDeploymentCheck.type === 'CHECK_FROM_INIT') this.initAppData(remoteDeploymentCheck);
      else this.inputAppHelper.showPopup(this.translate.instant('remote_deployment.no_update'), '');
      return;
    }
    const appUpdatesDTO: TAppUpdatesDTO = {
      featureType: appUpdates.featureType,
    };
    const modal = await this.modalCtrl.create({
      component: AppUpdatesComponent,
      componentProps: {
        appUpdatesDTO,
      },
      cssClass: 'm-modal__app-updates-modal',
      backdropDismiss: false,
    });
    await modal.present();
    await modal.onDidDismiss().then((res) => {
      const updateType: TUserActionForAppUpdate = res.data;
      if (updateType === 'UPDATE_LATER') this.initAppData(remoteDeploymentCheck);
      else this.syncPendingDataAndUpadate(remoteDeploymentCheck);
    });
  }

  syncPendingDataAndUpadate(remoteDeploymentCheck: TRemoteDeploymentCheck) {
    switch (remoteDeploymentCheck.appIdentifier) {
      case 'endline_qc_v2':
      case 'per_piece_qc_v2':
      case 'plan_qc_v2':
      case 'roaming_qc_v2': {
        this.imageFacade.forceSyncImagesAndUpdateApp(remoteDeploymentCheck, false);
        break;
      }
      case 'bundle_qc_v2': {
        this.bundleQcDBFacade.forceSyncBundlesAndUpdateApp(remoteDeploymentCheck, false);
        break;
      }
      case 'per_piece_line_in_v2':
      case 'md_point_v2':
      case 'line_in_v2':
      case 'md_point_per_piece_qc_v2': {
        this.inputAppClientDBFacade.forceSyncEventsAndUpdateApp(true, remoteDeploymentCheck, false);
        break;
      }
      case 'bulk_washing_in_v2':
      case 'bulk_washing_out_v2': {
        this.bulkCountAppFacade.forceSyncEventsAndUpdateApp(
          remoteDeploymentCheck.appIdentifier === 'bulk_washing_in_v2' ? 'SEND' : 'RECEIVE',
          remoteDeploymentCheck,
          false,
        );
        break;
      }
      case 'packing_v2':
      case 'cutbank_in_v2':
      case 'cutbank_out_v2':
      case 'aql_v2':
      case 'cpi':
      case 'per_piece_washing_in_v2':
      case 'per_piece_washing_out_v2':
      case 'fabric_inspection':
      case 'tv_dashboard_v2':
      case 'embellishment_in_v2':
      case 'embellishment_out_v2':
      case 'assembly_in':
      case 'component_line_in':
      case 'bundle_line_in_v2':
      case 'trim_inspection':
      case 'wip_packer_v2':
      case 'measurement_qc':
      case 'set_packs':
      case 'carton_packing': {
        this.store.dispatch(new ReFetchAppMasterAndClearState(remoteDeploymentCheck));
        break;
      }
      default: {
        this.inputAppHelper.showPopup(
          this.translate.instant('remote_deployment.config_error'),
          this.translate.instant('remote_deployment.update_not_configured'),
        );
      }
    }
  }

  initAppData(remoteDeploymentCheck: TRemoteDeploymentCheck) {
    if (remoteDeploymentCheck.type === 'CHECK_FROM_SETTINGS') return;
    switch (remoteDeploymentCheck.appIdentifier) {
      case 'bundle_qc_v2':
      case 'endline_qc_v2': {
        if (!remoteDeploymentCheck.isReload) {
          if (remoteDeploymentCheck.isInit) {
            this.inputAppClientDBFacade.emitLoadServerDefectivesSubscription(true);
            if (remoteDeploymentCheck.appIdentifier === 'bundle_qc_v2') {
              this.inputAppClientDBFacade.fetchIncompleteBundlesAndLoadBctxWithChainActions(
                { discardWipUpdates: false },
                {
                  chainWithRestoreBackedUpBctxAndThenLoadMasterdataList: {
                    wipOnly: false,
                    actualsOnly: true,
                    chainWithLoadMasterdataList: true,
                  },
                },
              );
            } else {
              this.inputAppClientDBFacade.loadBctxWithChainActions(
                { discardWipUpdates: false },
                {
                  chainWithRestoreBackedUpBctxAndThenLoadMasterdataList: {
                    wipOnly: false,
                    actualsOnly: false,
                    chainWithLoadMasterdataList: true,
                  },
                },
              );
            }
          } else {
            this.inputAppClientDBFacade
              .getClientDBAndBgDB()
              .pipe(take(1))
              .subscribe((res) => {
                if (EventUtil.hasAnyPendingEvents(res.userDB, res.bgDB)) {
                  this.masterdataListDBFacade.loadAllMasterdataListsAndCorrectiveActionsAndSilhouetteKeys(true);
                } else {
                  this.inputAppClientDBFacade.emitLoadServerDefectivesSubscription(true);
                  this.inputAppClientDBFacade.loadBctxWithChainActions(
                    { discardWipUpdates: false },
                    { chainWithLoadMasterdataLists: true },
                  );
                }
              });
          }
        }
        break;
      }
      case 'plan_qc_v2': {
        if (!remoteDeploymentCheck.isReload) {
          if (remoteDeploymentCheck.isInit) {
            this.inputAppClientDBFacade.emitLoadServerDefectivesSubscription(true);
            this.inputAppClientDBFacade.loadBctxPlanBased({
              chainWithLoadAllMasterDataList: true,
              masterDataListMetaData: { source: 'INIT' },
            });
          } else {
            this.inputAppClientDBFacade
              .getClientDBAndBgDB()
              .pipe(take(1))
              .subscribe((res) => {
                if (EventUtil.hasAnyPendingEvents(res.userDB, res.bgDB)) {
                  this.masterdataListDBFacade.loadAllMasterdataListsAndCorrectiveActionsAndSilhouetteKeys(true);
                } else {
                  this.inputAppClientDBFacade.emitLoadServerDefectivesSubscription(true);
                  this.inputAppClientDBFacade.loadBctxPlanBased({
                    chainWithLoadAllMasterDataList: true,
                    masterDataListMetaData: { source: 'RESET' },
                  });
                }
              });
          }
        }
        break;
      }
      case 'per_piece_line_in_v2': {
        if (!remoteDeploymentCheck.isReload) {
          if (remoteDeploymentCheck.isInit) {
            this.inputAppClientDBFacade.restoreAppStateForBackedUpBctx(
              true,
              { actualsOnly: true, wipOnly: false, chainWithLoadMasterdataList: false },
              false,
              false,
            );
          }
        }
        break;
      }
      case 'per_piece_qc_v2':
      case 'md_point_per_piece_qc_v2': {
        if (!remoteDeploymentCheck.isReload) {
          if (remoteDeploymentCheck.isInit) {
            this.inputAppClientDBFacade.emitLoadServerDefectivesSubscription(true);
            if (remoteDeploymentCheck.appIdentifier === 'per_piece_qc_v2') {
              this.inputAppClientDBFacade.loadBctxWithChainActions(
                { discardWipUpdates: true },
                {
                  chainWithRestoreBackedUpBctxAndThenLoadMasterdataList: {
                    wipOnly: false,
                    actualsOnly: true,
                    chainWithLoadMasterdataList: remoteDeploymentCheck.appIdentifier === 'per_piece_qc_v2',
                  },
                },
              );
            } else if (remoteDeploymentCheck.appIdentifier === 'md_point_per_piece_qc_v2') {
              this.inputAppClientDBFacade.restoreAppStateForBackedUpBctx(
                true,
                {
                  wipOnly: false,
                  actualsOnly: true,
                  chainWithLoadMasterdataList: false,
                },
                false,
                false,
              );
            }
          } else {
            this.inputAppClientDBFacade
              .getClientDBAndBgDB()
              .pipe(take(1))
              .subscribe((res) => {
                if (EventUtil.hasAnyPendingEvents(res.userDB, res.bgDB)) {
                  if (remoteDeploymentCheck.appIdentifier === 'per_piece_qc_v2') {
                    this.masterdataListDBFacade.loadAllMasterdataListsAndCorrectiveActionsAndSilhouetteKeys(true);
                  }
                } else {
                  this.inputAppClientDBFacade.emitLoadServerDefectivesSubscription(true);
                  if (remoteDeploymentCheck.appIdentifier === 'per_piece_qc_v2') {
                    this.inputAppClientDBFacade.loadBctxWithChainActions(
                      { discardWipUpdates: true },
                      { chainWithLoadMasterdataLists: remoteDeploymentCheck.appIdentifier === 'per_piece_qc_v2' },
                    );
                  }
                }
              });
          }
        }
        break;
      }
      case 'roaming_qc_v2': {
        if (!remoteDeploymentCheck.isReload) {
          if (remoteDeploymentCheck.isInit) {
            this.inputAppClientDBFacade
              .getIsPlanBasedBctx()
              .pipe(take(1))
              .subscribe((isPlanBasedBctx) => {
                if (isPlanBasedBctx) this.inputAppClientDBFacade.loadPlanBasedBctxForNonCountingApps();
                else {
                  this.masterdataListDBFacade.loadAllMasterdataLists(false);
                  this.inputAppClientDBFacade.loadBctxWithChainActions(
                    { discardWipUpdates: false },
                    { chainWithLoadMasterdataLists: true },
                  );
                }
              });
          }
        }
        break;
      }
      case 'bulk_washing_in_v2':
      case 'bulk_washing_out_v2': {
        if (!remoteDeploymentCheck.isReload) {
          if (remoteDeploymentCheck.isInit) {
            this.bulkCountAppFacade.initSyncStyleInfoOrReceiveCount(
              remoteDeploymentCheck.appIdentifier === 'bulk_washing_in_v2' ? 'SEND' : 'RECEIVE',
            );
          }
        }
        break;
      }
      case 'aql_v2': {
        if (!remoteDeploymentCheck.isReload) {
          this.inputAppClientDBFacade.loadBctxWithChainActions(
            { discardWipUpdates: true },
            { chainWithLoadMasterdataLists: true },
          );
        }
        break;
      }
      case 'line_in_v2': {
        if (!remoteDeploymentCheck.isReload) {
          this.inputAppClientDBFacade.getConfigurations().subscribe(({ isWipValidationEnabledLineIn }) => {
            const source = remoteDeploymentCheck.isInit ? 'INIT' : 'RESET';
            !isWipValidationEnabledLineIn
              ? this.inputAppClientDBFacade.loadBctxPlanBased({
                  chainWithLoadAllMasterDataList: true,
                  masterDataListMetaData: { source },
                })
              : this.inputAppClientDBFacade.loadBctxWithChainActions(
                  { discardWipUpdates: false },
                  {
                    chainWithRestoreBackedUpBctxAndThenLoadMasterdataList: {
                      wipOnly: false,
                      actualsOnly: !isWipValidationEnabledLineIn,
                      chainWithLoadMasterdataList: true,
                    },
                  },
                );
          });
        }
        break;
      }
      case 'wip_packer_v2':
      case 'measurement_qc': {
        this.initStoreFacade.acquireLockAndThenUpdateNextCacheResetTimestamp('PRE_INIT_PENDING');
        break;
      }
      default: {
      }
    }
  }
}
